<template>
  <div class="mainWrap mt-3 mb-5">
    <div class="row">
      <div class="col-12">
        <h3>Company Registration</h3>
        <p>
          Please fill out the form below to register your company. All fields
          marked with
          <span class="mandatory">*</span> are required.
        </p>
      </div>
    </div>
    <FormWithValidation
      v-model="createCompanyForm"
      :initial-state="initialState"
      :validation-rules-function="() => validationRules"
      :validation-messages="validationMessages"
      :field-labels-map="fieldLabelsMap"
      reset-button-text="Reset"
      :saved-callback="createCompany"
    >
      <template #userDetailsHeader>
        <h5>User Details</h5>
      </template>

      <template #username>
        <FormField id="username" mandatory name="username" label="User Name">
          <InputField
            id="username"
            :model-value="userStore.authUser?.userName"
            name="username"
            disabled
            class="form-control form-control-sm"
          />
        </FormField>
      </template>
      <template #email>
        <FormField id="email" mandatory name="email" label="Email">
          <InputField
            id="email"
            :model-value="userStore.authUser?.email"
            name="email"
            disabled
            class="form-control form-control-sm"
          />
        </FormField>
      </template>

      <template #firstName="slotProps">
        <FormField
          id="firstName"
          name="firstName"
          label="First Name"
          :mandatory="true"
          :v-field="slotProps.vForm.firstName"
          :validation-messages="slotProps.validationMessages.firstName"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.firstName)"
        >
          <InputField
            id="firstName"
            v-model.trim="createCompanyForm.firstName"
            name="firstName"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.firstName)"
            class="form-control form-control-sm"
          />
        </FormField>
      </template>

      <template #lastName="slotProps">
        <FormField
          id="lastName"
          name="lastName"
          label="Last Name"
          :mandatory="true"
          :v-field="slotProps.vForm.lastName"
          :validation-messages="slotProps.validationMessages.lastName"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.lastName)"
        >
          <InputField
            id="lastName"
            v-model.trim="createCompanyForm.lastName"
            name="lastName"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.lastName)"
            class="form-control form-control-sm"
          />
        </FormField>
      </template>

      <template #phoneNumber="slotProps">
        <FormField
          id="phoneNumber"
          name="phoneNumber"
          label="Phone Number"
          :mandatory="true"
          :v-field="slotProps.vForm.phoneNumber"
          :validation-messages="slotProps.validationMessages.phoneNumber"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.phoneNumber)"
        >
          <InputField
            id="phoneNumber"
            v-model.trim="createCompanyForm.phoneNumber"
            name="phoneNumber"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.phoneNumber)"
            class="form-control form-control-sm"
          />
        </FormField>
      </template>
      <template #companyDetailsHeader>
        <h5 class="mt-5">Company Legal Details</h5>
      </template>

      <template #companyName="slotProps">
        <FormField
          id="companyName"
          name="companyName"
          label="Company Name"
          :mandatory="true"
          :v-field="slotProps.vForm.companyName"
          :validation-messages="slotProps.validationMessages.companyName"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.companyName)"
        >
          <InputField
            id="companyName"
            v-model.trim="createCompanyForm.companyName"
            name="companyName"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.companyName)"
            class="form-control form-control-sm"
          />
        </FormField>
      </template>

      <template #country="slotProps">
        <FormField
          id="country"
          name="country"
          label="Country"
          :mandatory="true"
          :v-field="slotProps.vForm.country"
          :validation-messages="slotProps.validationMessages.country"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.country)"
        >
          <SelectField
            v-model="createCompanyForm.country"
            name="country"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.country)"
            :options="countriesStore.getCountryOptionsForSelectField"
            :nothing-selected-label="'Select your country'"
          />
        </FormField>
      </template>

      <template v-if="!addressIsInUSHub" #streetAndHouseNumber="slotProps">
        <div class="form-group row my-3 mb-2 align-items-center">
          <div class="col-12 col-lg-3">
            <label for="street" class="col-form-label"
              >Street & Number<span class="mandatory">*</span></label
            >
          </div>
          <div class="col-12 col-lg-9">
            <div class="row">
              <div class="col-7">
                <InlineFormField
                  id="street"
                  name="street"
                  :mandatory="true"
                  :v-field="slotProps.vForm.street"
                  :validation-messages="slotProps.validationMessages.street"
                  :field-errors="slotProps.fieldErrors"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.street)"
                >
                  <InputField
                    id="street"
                    v-model.trim="createCompanyForm.street"
                    name="street"
                    :class="slotProps.getIsInvalidClass(slotProps.vForm.street)"
                    class="form-control form-control-sm"
                  />
                </InlineFormField>
              </div>
              <div class="col-5">
                <InlineFormField
                  id="houseNumber"
                  name="houseNumber"
                  :mandatory="true"
                  :v-field="slotProps.vForm.houseNumber"
                  :validation-messages="
                    slotProps.validationMessages.houseNumber
                  "
                  :field-errors="slotProps.fieldErrors"
                  :class="
                    slotProps.getIsInvalidClass(slotProps.vForm.houseNumber)
                  "
                >
                  <InputField
                    id="houseNumber"
                    v-model.trim="createCompanyForm.houseNumber"
                    :class="
                      slotProps.getIsInvalidClass(slotProps.vForm.houseNumber)
                    "
                    name="houseNumber"
                    class="form-control form-control-sm"
                  />
                </InlineFormField>
              </div>
            </div>
          </div>
        </div>
        <FormField
          id="additionalInfo"
          name="additionalInfo"
          label="Additional Info"
          :v-field="slotProps.vForm.additionalInfo"
          :validation-messages="slotProps.validationMessages.additionalInfo"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.additionalInfo)"
        >
          <InputField
            id="additionalInfo"
            v-model.trim="createCompanyForm.additionalInfo"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.additionalInfo)"
            name="additionalInfo"
            class="form-control form-control-sm"
          />
        </FormField>
        <div class="form-group row my-3 mb-2 align-items-center">
          <div class="col-sm-4 col-lg-3">
            <label for="postalCode" class="col-form-label"
              >Postal Code and City<span class="mandatory">*</span></label
            >
          </div>
          <div class="col-12 col-lg-9">
            <div class="row">
              <div class="col-5">
                <InlineFormField
                  id="postalCode"
                  name="postalCode"
                  label="Postal Code"
                  :mandatory="true"
                  :v-field="slotProps.vForm.postalCode"
                  :validation-messages="slotProps.validationMessages.postalCode"
                  :field-errors="slotProps.fieldErrors"
                  :class="
                    slotProps.getIsInvalidClass(slotProps.vForm.postalCode)
                  "
                >
                  <InputField
                    id="postalCode"
                    v-model.trim="createCompanyForm.postalCode"
                    :class="
                      slotProps.getIsInvalidClass(slotProps.vForm.postalCode)
                    "
                    name="postalCode"
                    class="form-control form-control-sm"
                  />
                </InlineFormField>
              </div>
              <div class="col-7">
                <InlineFormField
                  id="city"
                  name="city"
                  :mandatory="true"
                  :v-field="slotProps.vForm.city"
                  :validation-messages="slotProps.validationMessages.city"
                  :field-errors="slotProps.fieldErrors"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.city)"
                >
                  <InputField
                    id="city"
                    v-model.trim="createCompanyForm.city"
                    :class="slotProps.getIsInvalidClass(slotProps.vForm.city)"
                    name="city"
                    class="form-control form-control-sm"
                  />
                </InlineFormField>
              </div>
            </div>
          </div>
        </div>
        <FormField
          v-if="addressIsWhitelisted"
          id="vatIdNumber"
          name="vatIdNumber"
          label="VAT-ID"
          :mandatory="true"
          :v-field="slotProps.vForm.vatIdNumber"
          :validation-messages="slotProps.validationMessages.vatIdNumber"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.vatIdNumber)"
        >
          <InputField
            id="vatIdNumber"
            v-model.trim="createCompanyForm.vatIdNumber"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.vatIdNumber)"
            name="vatIdNumber"
            class="form-control form-control-sm"
          />
        </FormField>

        <FormField
          v-if="addressCanSpecifyPXCNumber"
          id="pxcNumber"
          name="pxcNumber"
          label="PXC Customer Number"
          :v-field="slotProps.vForm.pxcNumber"
          :validation-messages="slotProps.validationMessages.pxcNumber"
          :field-errors="slotProps.fieldErrors"
          help-text="If you do not have a PxC Customer number, please contact our support to prove your entrepreneurship"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.pxcNumber)"
        >
          <div class="row">
            <div class="col-12">
              <InputField
                id="pxcNumber"
                v-model.trim="createCompanyForm.pxcNumber"
                :class="slotProps.getIsInvalidClass(slotProps.vForm.pxcNumber)"
                name="pxcNumber"
                class="form-control form-control-sm"
              />
              <PhCheck
                v-if="companyStore.userCompany?.pxcNumberVerified === false"
                size="24"
                weight="bold"
                class="mb-1"
                style="color: green"
                title="verified"
              />
              <PhX
                v-if="companyStore.userCompany?.pxcNumberVerified === false"
                size="24"
                weight="bold"
                class="mb-1"
                style="color: red"
                title="not verified"
              />
            </div>
          </div>
        </FormField>
      </template>

      <template v-if="addressIsInUSHub" #usHubAddress="slotProps">
        <FormField
          id="street"
          name="street"
          :mandatory="true"
          label="Address Line 1"
          :v-field="slotProps.vForm.street"
          :validation-messages="slotProps.validationMessages.street"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.street)"
        >
          <InputField
            id="additionalInfo"
            v-model.trim="createCompanyForm.street"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.street)"
            name="street"
            class="form-control form-control-sm"
          />
        </FormField>
        <FormField
          id="additionalInfo"
          name="additionalInfo"
          label="Address Line 2"
          :v-field="slotProps.vForm.additionalInfo"
          :validation-messages="slotProps.validationMessages.additionalInfo"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.additionalInfo)"
        >
          <InputField
            id="additionalInfo"
            v-model.trim="createCompanyForm.additionalInfo"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.additionalInfo)"
            name="additionalInfo"
            class="form-control form-control-sm"
          />
        </FormField>
        <div class="form-group row my-3 mb-2 align-items-center">
          <div class="col-sm-4 col-lg-3">
            <label for="city" class="col-form-label"
              >City and ZIP<span class="mandatory">*</span></label
            >
          </div>
          <div class="col-12 col-lg-9">
            <div class="row">
              <div class="col-7">
                <InlineFormField
                  id="city"
                  name="city"
                  :mandatory="true"
                  :v-field="slotProps.vForm.city"
                  :validation-messages="slotProps.validationMessages.city"
                  :field-errors="slotProps.fieldErrors"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.city)"
                >
                  <InputField
                    id="city"
                    v-model.trim="createCompanyForm.city"
                    :class="slotProps.getIsInvalidClass(slotProps.vForm.city)"
                    name="city"
                    class="form-control form-control-sm"
                  />
                </InlineFormField>
              </div>
              <div class="col-5">
                <InlineFormField
                  id="postalCode"
                  name="postalCode"
                  label="Postal Code"
                  :mandatory="true"
                  :v-field="slotProps.vForm.postalCode"
                  :validation-messages="slotProps.validationMessages.postalCode"
                  :field-errors="slotProps.fieldErrors"
                  :class="
                    slotProps.getIsInvalidClass(slotProps.vForm.postalCode)
                  "
                >
                  <InputField
                    id="postalCode"
                    v-model.trim="createCompanyForm.postalCode"
                    :class="
                      slotProps.getIsInvalidClass(slotProps.vForm.postalCode)
                    "
                    name="postalCode"
                    class="form-control form-control-sm"
                  />
                </InlineFormField>
              </div>
            </div>
          </div>
        </div>
        <FormField
          v-if="addressIsInUSHub && !addressIsCanada"
          id="state"
          name="state"
          label="State"
          :mandatory="true"
          :v-field="slotProps.vForm.state"
          :validation-messages="slotProps.validationMessages.state"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.state)"
        >
          <SelectField
            v-model="createCompanyForm.state"
            name="state"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.state)"
            :options="addressIsUS ? usStates : mxStates"
            :nothing-selected-label="'Select your state'"
          />
        </FormField>
        <FormField
          v-if="addressIsInUSHub && addressIsCanada"
          id="province"
          name="province"
          label="Province"
          :mandatory="true"
          :v-field="slotProps.vForm.province"
          :validation-messages="slotProps.validationMessages.province"
          :field-errors="slotProps.fieldErrors"
          :class="slotProps.getIsInvalidClass(slotProps.vForm.province)"
        >
          <SelectField
            v-model="createCompanyForm.province"
            name="province"
            :class="slotProps.getIsInvalidClass(slotProps.vForm.province)"
            :options="caProvinces"
            :nothing-selected-label="'Select your province'"
          />
        </FormField>
      </template>

      <template #cancel-button>
        <StoreButton @click="router.go(-1)"> Back </StoreButton>
      </template>
    </FormWithValidation>
  </div>
</template>

<script lang="ts" setup>
import userIsLoggedIn from "~/middleware/userIsLoggedIn";
import userIsOnOwnUserPage from "~/middleware/userIsOnOwnUserPage";
import userHasNoCompany from "~/middleware/userHasNoCompany";
import { useUserStore } from "~/stores/userstore";
import { useCompanyStore } from "~/stores/companystore";
import { useCountriesStore } from "~/stores/countriesstore";
import FormWithValidation from "~/components/shared/forms/FormWithValidation.vue";
import {
  type CreateCompanyForm,
  fieldLabelsMap,
  getCreateCompanyFormRules,
  validationMessages,
} from "~/validations/create-company";
import type {
  WebModelGetCountry,
  WebModelPostCompany,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { useToastStore } from "~/stores/ui/toaststore";
import { useUiStore } from "~/stores/ui/uistore";
import FormField from "~/components/shared/forms/FormField.vue";
import InputField from "~/components/shared/inputfields/InputField.vue";
import SelectField from "~/components/shared/inputfields/SelectField.vue";
import InlineFormField from "~/components/shared/forms/InlineFormField.vue";
import {
  canadaProvinces,
  mexicoStates,
  unitedStates,
} from "~/helpers/usHubStatesProvinces";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import { PhCheck, PhX } from "@phosphor-icons/vue";

definePageMeta({
  middleware: [userIsLoggedIn, userIsOnOwnUserPage, userHasNoCompany],
});

const userStore = useUserStore();
const companyStore = useCompanyStore();
const router = useRouter();
const countriesStore = useCountriesStore();

const initialState = computed(() => {
  return {
    firstName: userStore.authUser?.firstName || "",
    lastName: userStore.authUser?.lastName || "",
    phoneNumber: userStore.authUser?.phoneNumber || "",

    companyName: "",
    country: userStore.authUser?.country?.iso || "",
    vatIdNumber: "",
    pxcNumber: "",

    additionalInfo: "",
    street: "",
    houseNumber: "",
    postalCode: "",
    city: "",
    state: "",
    province: "",
  };
});

// Store the form data and other reactive values
const createCompanyForm = reactive<CreateCompanyForm>({
  ...initialState.value,
});

const validationRules = computed(() => {
  return getCreateCompanyFormRules(createCompanyForm);
});

const createCompany = async () => {
  let state = undefined;
  let vatIdNumber = undefined;
  if (addressIsInUSHub.value) {
    state = addressIsCanada.value
      ? createCompanyForm.province
      : createCompanyForm.state;
  }
  if (addressIsWhitelisted.value) {
    vatIdNumber = createCompanyForm.vatIdNumber;
  }

  const webModelPostCompany: WebModelPostCompany = {
    name: createCompanyForm.companyName,
    vatIdNumber: vatIdNumber,
    pxcNumber: createCompanyForm.pxcNumber,
    webModelUpdateUser: {
      phoneNumber: createCompanyForm.phoneNumber,
      firstName: createCompanyForm.firstName,
      lastName: createCompanyForm.lastName,
    },
    webModelPostNewAddress: {
      country: createCompanyForm.country,
      state: state,
      city: createCompanyForm.city,
      postalCode: createCompanyForm.postalCode,
      street: createCompanyForm.street,
      houseNumber: createCompanyForm.houseNumber,
      additionalInfo: createCompanyForm.additionalInfo,
    },
  };
  await companyStore.createCompany(webModelPostCompany);
  useToastStore().addToastWithMessageAndTitle(
    "Success",
    "Company " + createCompanyForm.companyName + " successfully created.",
    "success",
  );
  useUiStore().isFormDirty = false;
  router.back();
};

const selectedCountry: ComputedRef<WebModelGetCountry | undefined> = computed(
  () => {
    return countriesStore.countries.find(
      (country) => country.iso === createCompanyForm.country,
    );
  },
);

const addressIsWhitelisted: ComputedRef<boolean> = computed(() => {
  return selectedCountry.value?.status === "Full";
});

const addressCanSpecifyPXCNumber: ComputedRef<boolean> = computed(() => {
  return ["GB", "NO", "CH"].includes(selectedCountry.value?.iso as string);
});

const addressIsInUSHub: ComputedRef<boolean> = computed(() => {
  return (
    selectedCountry.value?.iso === "US" ||
    selectedCountry.value?.iso === "CA" ||
    selectedCountry.value?.iso === "MX"
  );
});

const addressIsCanada: ComputedRef<boolean> = computed(() => {
  return selectedCountry.value?.iso === "CA";
});

const addressIsUS: ComputedRef<boolean> = computed(() => {
  return selectedCountry.value?.iso === "US";
});

const caProvinces = computed(() =>
  canadaProvinces.map((province) => ({
    value: province.code,
    text: province.name,
  })),
);

const usStates = computed(() =>
  unitedStates.map((state) => ({
    value: state.code,
    text: state.name,
  })),
);

const mxStates = computed(() =>
  mexicoStates.map((state) => ({
    value: state.code,
    text: state.name,
  })),
);

useSeoMeta({ title: `PLCnext Store | Create Company` });
</script>
